import { SimpleGrid, Card, Text, Group, RingProgress, Stack, Button, Box, Title } from '@mantine/core';
import { AreaChart, BarChart } from '@mantine/charts';
import {
  IconUsers, IconSchool, IconUserPlus, IconCash, IconCalendarCheck,
  IconChecklist, IconUserCheck, IconBriefcase, IconUsersGroup,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useRevenue } from '../hooks/useLookups';
import { StatCard } from '../components/StatCard';
import { SectionCard } from '../components/SectionCard';
import { GenderCard, type GenderSplit } from '../components/GenderCard';
import { CardsSkeleton } from '../components/TableSkeleton';
import { formatSom, formatSomShort } from '../lib/format';
import { useAuth } from '../contexts/AuthContext';
import { PRODUCT_NAME } from '../lib/brand';
import { canAccess, isTenantStaff } from '../lib/permissions';

interface DashboardData {
  students: { total: number; active: number };
  classes: number;
  staff: number;
  teachers: number;
  admissionsOpen: number;
  attendanceToday: {
    present: number;
    absent: number;
    late: number;
    /** Faol o'quvchilar soni — foiz shundan hisoblanadi. */
    total: number;
    /** Dam olish kunida foiz ko'rsatilmaydi. */
    ishKuni: boolean;
  };
  genderStudents: GenderSplit;
  genderStaff: GenderSplit;
  genderParents: GenderSplit;
  revenueThisMonth: number | null;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => (await api.get<DashboardData>('/dashboard')).data,
  });

  const canReports = canAccess(user?.role, 'reports');
  const year = String(new Date().getFullYear());
  const { data: revenue } = useRevenue(year, canReports);
  const { data: byClass } = useQuery({
    queryKey: ['dash-byclass'],
    // Moliyaviy emas — administrator ham ko'radi (backend: STAFF + MONEY)
    enabled: canReports || isTenantStaff(user),
    queryFn: async () => (await api.get<{ className: string; count: number }[]>('/reports/students-by-class')).data,
  });

  const present = data?.attendanceToday.present ?? 0;
  const absent = data?.attendanceToday.absent ?? 0;
  const late = data?.attendanceToday.late ?? 0;
  /* Foiz BELGILANGANLARDAN emas, BARCHA faol o'quvchidan hisoblanadi.
     Ilgari `present + absent` bo'luvchi edi va bazada bitta ham yozuv
     bo'lmagani uchun natija doim 0% chiqardi. Davomat esa "hamma keldi"
     holatidan boshlanadi: belgilanmagan o'quvchi — kelgan o'quvchi. */
  const jamiOquvchi = data?.attendanceToday.total ?? 0;
  const ishKuni = data?.attendanceToday.ishKuni ?? true;
  const presentPct = jamiOquvchi ? Math.round((present / jamiOquvchi) * 100) : 0;

  const quickActions = [
    { label: t('dashboard.qStudent'), icon: IconUsers, to: '/students', section: 'students' as const },
    { label: t('dashboard.qAttendance'), icon: IconCalendarCheck, to: '/attendance', section: 'attendance' as const },
    { label: t('dashboard.qGrade'), icon: IconChecklist, to: '/grades', section: 'grades' as const },
    { label: t('dashboard.qPayment'), icon: IconCash, to: '/payments', section: 'payments' as const },
  ].filter((a) => canAccess(user?.role, a.section));

  return (
    <Stack gap="lg">
      {/* Salomlashuv banneri */}
      <Card
        radius="lg"
        p="xl"
        style={{ background: 'linear-gradient(120deg, #4c3fb8 0%, #6C5CE7 100%)', border: 'none' }}
      >
        <Group justify="space-between" align="flex-end">
          <Box>
            <Text c="white" opacity={0.8} size="sm">{t('dashboard.welcome')},</Text>
            <Title order={2} c="white">{user?.fullName}</Title>
            <Text c="white" opacity={0.85} size="sm" mt={4}>{t('dashboard.subtitle', { school: user?.tenantName || PRODUCT_NAME })}</Text>
          </Box>
          {quickActions.length > 0 && (
            <Group gap="xs" visibleFrom="sm">
              {quickActions.slice(0, 2).map((a) => (
                <Button key={a.to} variant="white" color="dark" leftSection={<a.icon size={16} />} onClick={() => navigate(a.to)}>
                  {a.label}
                </Button>
              ))}
            </Group>
          )}
        </Group>
      </Card>

      {/* Statistika */}
      <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="lg">
        {isLoading ? (
          <CardsSkeleton count={4} height={104} />
        ) : (
          <>
            <StatCard icon={IconUsers} color="blue" label={t('dashboard.students')} value={data?.students.total ?? 0} hint={`${data?.students.active ?? 0} ${t('dashboard.active')}`} onClick={() => navigate('/students')} />
            <StatCard icon={IconSchool} color="indigo" label={t('dashboard.classes')} value={data?.classes ?? 0} onClick={() => navigate('/classes')} />
            <StatCard icon={IconUserCheck} color="teal" label={t('dashboard.teachers')} value={data?.teachers ?? 0} onClick={() => navigate('/staff')} />
            <StatCard icon={IconUserPlus} color="orange" label={t('dashboard.admissions')} value={data?.admissionsOpen ?? 0} onClick={() => canAccess(user?.role, 'admissions') && navigate('/admissions')} />
          </>
        )}
      </SimpleGrid>

      {/* Jins bo'yicha taqsimot — o'quvchilar / xodimlar / ota-onalar */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
        {isLoading ? (
          <CardsSkeleton count={3} height={200} />
        ) : (
          <>
            <GenderCard icon={IconUsers} title={t('dashboard.genderStudents')} data={data?.genderStudents}
              maleLabel={t('dashboard.boys')} femaleLabel={t('dashboard.girls')} />
            <GenderCard icon={IconBriefcase} title={t('dashboard.genderStaff')} data={data?.genderStaff}
              maleLabel={t('dashboard.male')} femaleLabel={t('dashboard.female')} />
            <GenderCard icon={IconUsersGroup} title={t('dashboard.genderParents')} data={data?.genderParents}
              maleLabel={t('dashboard.fathers')} femaleLabel={t('dashboard.mothers')} />
          </>
        )}
      </SimpleGrid>

      {/* Davomat + moliya */}
      <SimpleGrid cols={{ base: 1, md: data?.revenueThisMonth != null ? 2 : 1 }} spacing="lg">
        <SectionCard
          title={t('dashboard.todayAttendance')}
          subtitle={
            ishKuni
              ? `${t('dashboard.present')}: ${present} · ${t('dashboard.absent')}: ${absent}` +
                (late ? ` · ${t('att.LATE')}: ${late}` : '')
              : t('dashboard.restDay')
          }
          icon={IconCalendarCheck}
          color="teal"
        >
          <Group justify="center">
            <RingProgress
              size={110} thickness={11} roundCaps
              sections={[{ value: ishKuni ? presentPct : 0, color: ishKuni ? 'teal' : 'gray' }]}
              label={
                <Text ta="center" fw={800} fz="lg" c={ishKuni ? undefined : 'dimmed'}>
                  {ishKuni ? `${presentPct}%` : '—'}
                </Text>
              }
            />
          </Group>
        </SectionCard>

        {data?.revenueThisMonth != null && (
          <SectionCard
            title={t('dashboard.revenue')}
            subtitle={t('dashboard.revenueSub')}
            icon={IconCash}
            color="green"
          >
            <Group justify="center" h="100%">
              <Text fz={30} fw={800} c="green.7">{formatSom(data.revenueThisMonth)}</Text>
            </Group>
          </SectionCard>
        )}
      </SimpleGrid>

      {/* Statistika grafiklari (moliya/boshqaruv rollariga) */}
      {canReports && (
        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
          <SectionCard title={`${t('dashboard.revenue')} · ${year}`} icon={IconCash} color="green">
            {revenue?.length ? (
              <AreaChart
                h={250}
                data={revenue.map((r) => ({ oy: monthNames[Number(r.month.slice(5)) - 1] ?? r.month.slice(5), value: r.total }))}
                dataKey="oy"
                series={[{ name: 'value', color: 'brand.6', label: t('charts.income') }]}
                curveType="natural"
                withGradient
                withDots={false}
                valueFormatter={(v) => formatSom(v)}
                yAxisProps={{ width: 58, tickFormatter: (v: number) => formatSomShort(v) }}
              />
            ) : (
              <Text c="dimmed" size="sm">—</Text>
            )}
          </SectionCard>
          <SectionCard title={t('nav.classes')} icon={IconSchool} color="grape">
            {byClass?.length ? (
              <BarChart
                h={250}
                data={byClass.map((c) => ({ sinf: c.className, soni: c.count }))}
                dataKey="sinf"
                series={[{ name: 'soni', color: 'grape.6', label: t('charts.count') }]}
                tickLine="y"
              />
            ) : (
              <Text c="dimmed" size="sm">—</Text>
            )}
          </SectionCard>
        </SimpleGrid>
      )}
    </Stack>
  );
}
